/**
 * Starter prompts for the empty state.
 *
 * The old screen offered two cards that were navigation, not questions — they
 * taught the user nothing about what the assistant could answer. These are real
 * questions, and the first few are built from the user's OWN workspace, so a
 * user with 412 unlabeled frames in "line-scan-v2" is offered that exact
 * question instead of a generic one they have to translate themselves.
 *
 * Strings resolve through i18n (`labelKey` / `promptKey` with `vars`), so both
 * the label and the question the user sends arrive in their language. That is
 * also why the prompt text is a translated template rather than a built string:
 * the backend mirrors the language of the question it receives.
 */

/** Flatten the nested workspace into one row per model version. */
const flattenVersions = (workspace) => {
  const rows = [];
  (workspace?.projects || []).forEach((project) => {
    (project.models || []).forEach((model) => {
      (model.versions || []).forEach((version) => {
        const status = version.labelingStatus || {};
        rows.push({
          projectName: project.project_name || 'untitled',
          modelName: model.model_name || 'untitled',
          modelType: model.model_type || 'unknown',
          version: version.version || '1.0',
          total: status.totalImages || 0,
          labeled: status.labeledImages || 0,
          unlabeled: status.unlabeledImages || 0,
          classes: (version.classes || []).length,
          trainingStatus: (version.trainingStatus || 'pending').toLowerCase(),
          metrics: version.performanceMetrics || null,
          createdOn: version.createdOn || null,
        });
      });
    });
  });
  return rows;
};

/** Human name for a model type, for use inside a prompt sentence. */
const TYPE_LABEL_KEY = {
  classification: 'assistant.modelType.classification',
  bounding_box: 'assistant.modelType.detection',
  segmentation: 'assistant.modelType.segmentation',
};

/**
 * Generic prompts, always available.
 *
 * Ordered so the two most common questions come first — those are what a new
 * user actually opens this screen to ask.
 */
const GENERIC = [
  {
    id: 'compare',
    icon: 'fa-layer-group',
    labelKey: 'assistant.suggest.compare.label',
    promptKey: 'assistant.suggest.compare.prompt',
  },
  {
    id: 'dataset-size',
    icon: 'fa-database',
    labelKey: 'assistant.suggest.datasetSize.label',
    promptKey: 'assistant.suggest.datasetSize.prompt',
  },
  {
    id: 'split',
    icon: 'fa-chart-line',
    labelKey: 'assistant.suggest.split.label',
    promptKey: 'assistant.suggest.split.prompt',
  },
  {
    id: 'classes',
    icon: 'fa-tag',
    labelKey: 'assistant.suggest.classes.label',
    promptKey: 'assistant.suggest.classes.prompt',
  },
  {
    id: 'video',
    icon: 'fa-image',
    labelKey: 'assistant.suggest.video.label',
    promptKey: 'assistant.suggest.video.prompt',
  },
  {
    id: 'metrics',
    icon: 'fa-crosshairs',
    labelKey: 'assistant.suggest.metrics.label',
    promptKey: 'assistant.suggest.metrics.prompt',
  },
];

/** Prompts for someone whose workspace is empty. */
const ONBOARDING = [
  {
    id: 'first-project',
    icon: 'fa-rocket',
    labelKey: 'assistant.suggest.firstProject.label',
    promptKey: 'assistant.suggest.firstProject.prompt',
  },
  {
    id: 'pick-type',
    icon: 'fa-layer-group',
    labelKey: 'assistant.suggest.pickType.label',
    promptKey: 'assistant.suggest.pickType.prompt',
  },
];

const TOTAL_SUGGESTIONS = 6;
/** Below this many labeled images per class, results are unreliable. */
const THIN_PER_CLASS = 50;

/**
 * Build the suggestion list.
 *
 * Returns at most `TOTAL_SUGGESTIONS` entries: workspace-derived ones first,
 * then generic ones to fill. `t` is passed in rather than imported so this stays
 * a pure function and can be unit-tested without a React tree.
 */
export const buildSuggestions = ({ workspace, t }) => {
  const rows = flattenVersions(workspace);
  const contextual = [];

  const resolve = (entry) => ({
    id: entry.id,
    icon: entry.icon,
    kind: entry.kind || 'generic',
    label: t(entry.labelKey, entry.vars),
    prompt: t(entry.promptKey, entry.vars),
  });

  if (!rows.length) {
    // Nothing to ground on. Onboarding first, then the generic set.
    return [...ONBOARDING, ...GENERIC].slice(0, TOTAL_SUGGESTIONS).map(resolve);
  }

  // 1. A finished training run is the most valuable thing to talk about: the
  //    numbers are on screen elsewhere but nothing explains what to do next.
  const trained = rows
    .filter((row) => row.trainingStatus === 'completed')
    .sort((a, b) => b.total - a.total)[0];
  if (trained) {
    contextual.push({
      id: 'ctx-review-training',
      icon: 'fa-chart-line',
      kind: 'context',
      labelKey: 'assistant.suggest.ctxReview.label',
      promptKey: 'assistant.suggest.ctxReview.prompt',
      vars: { model: trained.modelName, version: trained.version },
    });
  }

  // 2. The biggest pile of unlabeled images — the actual blocker on training.
  const unlabeled = rows
    .filter((row) => row.unlabeled > 0)
    .sort((a, b) => b.unlabeled - a.unlabeled)[0];
  if (unlabeled) {
    contextual.push({
      id: 'ctx-unlabeled',
      icon: 'fa-tag',
      kind: 'context',
      labelKey: 'assistant.suggest.ctxUnlabeled.label',
      promptKey: 'assistant.suggest.ctxUnlabeled.prompt',
      vars: {
        count: unlabeled.unlabeled,
        model: unlabeled.modelName,
        total: unlabeled.total,
      },
    });
  }

  // 3. A dataset that is labeled but too thin to train well. Catching this
  //    before a run saves the user a wasted GPU slot and a confusing result.
  const thin = rows
    .filter(
      (row) =>
        row.labeled > 0 &&
        row.classes > 0 &&
        row.trainingStatus !== 'completed' &&
        row.labeled / row.classes < THIN_PER_CLASS,
    )
    .sort((a, b) => a.labeled / a.classes - b.labeled / b.classes)[0];
  if (thin) {
    contextual.push({
      id: 'ctx-thin',
      icon: 'fa-exclamation-triangle',
      kind: 'context',
      labelKey: 'assistant.suggest.ctxThin.label',
      promptKey: 'assistant.suggest.ctxThin.prompt',
      vars: { labeled: thin.labeled, classes: thin.classes, model: thin.modelName },
    });
  }

  // 4. Fully labeled and never trained — the one-click-away case.
  const ready = rows.find(
    (row) => row.total > 0 && row.unlabeled === 0 && row.trainingStatus !== 'completed',
  );
  if (ready) {
    contextual.push({
      id: 'ctx-ready',
      icon: 'fa-graduation-cap',
      kind: 'context',
      labelKey: 'assistant.suggest.ctxReady.label',
      promptKey: 'assistant.suggest.ctxReady.prompt',
      vars: {
        model: ready.modelName,
        count: ready.total,
        type: t(TYPE_LABEL_KEY[ready.modelType] || 'assistant.modelType.unknown'),
      },
    });
  }

  // Keep at most three grounded prompts so the generic set stays visible — a
  // user whose question is not about their existing data still needs a way in.
  const picked = contextual.slice(0, 3);
  const usedIds = new Set(picked.map((entry) => entry.id));
  const filler = GENERIC.filter((entry) => !usedIds.has(entry.id));
  return [...picked, ...filler].slice(0, TOTAL_SUGGESTIONS).map(resolve);
};

/** Totals for the workspace panel. Exported for reuse and testing. */
export const summariseWorkspace = (workspace) => {
  const rows = flattenVersions(workspace);
  return {
    projects: workspace?.projects?.length || 0,
    models: (workspace?.projects || []).reduce(
      (sum, project) => sum + (project.models || []).length,
      0,
    ),
    versions: rows.length,
    images: rows.reduce((sum, row) => sum + row.total, 0),
    labeled: rows.reduce((sum, row) => sum + row.labeled, 0),
    unlabeled: rows.reduce((sum, row) => sum + row.unlabeled, 0),
    trained: rows.filter((row) => row.trainingStatus === 'completed').length,
  };
};

export { flattenVersions };
